import * as React from "react";
import { useQuery } from "@apollo/client";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Dropdown from "react-bootstrap/Dropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faEdit,
  faCog,
  faPlus,
  faGripVertical,
  faDownload,
  faFileExcel,
  faCopy,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import { gettext } from "i18n";
import { useModalContext } from "state/modal";
import ControlledDateRangeSelector from "components/dates/ControlledDateRangeSelector";
import { SearchableStationPicker } from "components/common";
import {
  GET_MEASUREMENT_STATION_META,
  GET_DATASOURCES_BY_STATION,
} from "utils/stations";
import {
  GetMeasurementStationMetaQuery,
  GetMeasurementStationMetaQueryVariables,
} from "utils/__generated__/GetMeasurementStationMetaQuery";
import {
  GetDatasourcesByStation,
  GetDatasourcesByStationVariables,
} from "utils/__generated__/GetDatasourcesByStation";
import {
  STATIC_DATE_WIDGETS,
  WIDGET_TEMPLATES,
  WidgetTemplate,
} from "utils/widgets";
import { toUTC } from "utils/dates";
import { insertIntoList } from "utils/arrays";
import {
  useDraggable,
  useScopedDragEnd,
  useDragSelector,
  DragPayload,
  DropPosition,
} from "utils/dnd";
import { WidgetDropZone, RowDropZone } from "./DropZones";
import classNames from "classnames";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import StationDashboard from "./StationDashboard";
import CommentWidget from "./CommentWidget";
import {
  SIZE_PRESETS,
  DEFAULT_COL_SPAN,
  getSizePreset,
  calculateWidgetWidthDuringDrag,
  calculateWidgetDrop,
  updateWidgetInStructure,
  useMediaQuery,
  WidgetStructure,
  WidgetRowStructure,
  StationStructure,
} from "utils/dashboards";

export type {
  WidgetStructure,
  WidgetRowStructure,
  StationStructure,
} from "utils/dashboards";

interface ReportingDashboardStationProps {
  station: StationStructure;
  stationIndex: number;
  editWidgets: boolean;
  editedStructure: any[] | null;
  setEditedStructure: React.Dispatch<React.SetStateAction<any[] | null>>;
  setChangesMade: React.Dispatch<React.SetStateAction<boolean>>;
  onWidgetDateRangeChange: (
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
    newRange: [Date, Date],
  ) => void;
  onOpenWidgetSettings: (
    widget: WidgetStructure,
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
  ) => void;
  onOpenDatasourceSettings: (
    sourceUUID: string,
    sourceName: string,
    sourceIndex: number,
    widget: WidgetStructure,
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
  ) => void;
  canEditVisualSettings?: boolean;
  onExportWidget?: (widgetElement: HTMLElement, widgetTitle: string) => void;
  onExportWidgetExcel?: (widget: WidgetStructure) => void;
  dashboardId?: number;
  onCopyWidget?: (
    widget: WidgetStructure,
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
  ) => void;
}

interface DraggableWidgetProps {
  widget: WidgetStructure;
  widgetIndex: number;
  rowIndex: number;
  row: WidgetRowStructure;
  editWidgets: boolean;
  identifier: string;
  stationId: number | undefined;
  stationIndex: number;
  canEditVisualSettings: boolean;
  editedStructure: any[] | null;
  setEditedStructure: React.Dispatch<React.SetStateAction<any[] | null>>;
  setChangesMade: React.Dispatch<React.SetStateAction<boolean>>;
  isTouchDevice: boolean;
  isMobileViewport: boolean;
  stationWidgetRows: any[];
  onOpenDatasourceSettings: (
    sourceUUID: string,
    sourceName: string,
    sourceIndex: number,
    widget: WidgetStructure,
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
  ) => void;
  onWidgetDateRangeChange: (
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
    newRange: [Date, Date],
  ) => void;
  showEditWidget: (widget: any, rowIndex: number, widgetIndex: number) => void;
  handleWidgetResize: (
    rowIndex: number,
    widgetIndex: number,
    newColSpan: number,
  ) => void;
  onDeleteWidget: (idx: number, widgetGroupIdx: number) => void;
  quickAddRowIndex: number | null;
  setQuickAddRowIndex: (index: number | null) => void;
  selectedStationIdentifier: string;
  setSelectedStationIdentifier: (id: string) => void;
  selectedStationId: number | null;
  setSelectedStationId: (id: number | null) => void;
  datasourcesData: GetDatasourcesByStation | undefined;
  handleQuickAddSource: (
    rowIndex: number,
    widgetIndex: number,
    sourceUUID: string,
  ) => void;
  handleCommentWidgetChange: (
    rowIndex: number,
    widgetIndex: number,
    newValues: {
      title: string;
      content: string;
      fontSize: number;
      fontColor: string;
      backgroundColor: string;
    },
  ) => void;
  onOpenWidgetSettings: (
    widget: WidgetStructure,
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
  ) => void;
  onExportWidget?: (widgetElement: HTMLElement, widgetTitle: string) => void;
  onExportWidgetExcel?: (widget: WidgetStructure) => void;
  moveWidget: (
    widgetIdx: number,
    rowIdx: number,
    direction: "left" | "right" | "up" | "down",
    isMobileViewport?: boolean,
  ) => void;
  dashboardId?: number;
  onCopyWidget?: (
    widget: WidgetStructure,
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
  ) => void;
}

const getWidgetTemplate = (key: string): WidgetTemplate | undefined => {
  return WIDGET_TEMPLATES.find((t) => t.key === key);
};

const DraggableWidget: React.FC<DraggableWidgetProps> = React.memo(
  ({
    widget,
    widgetIndex,
    rowIndex,
    row,
    editWidgets,
    identifier,
    stationId,
    stationIndex,
    canEditVisualSettings,
    editedStructure,
    setEditedStructure,
    setChangesMade,
    isTouchDevice,
    isMobileViewport,
    stationWidgetRows,
    onOpenDatasourceSettings,
    onWidgetDateRangeChange,
    showEditWidget,
    handleWidgetResize,
    onDeleteWidget,
    quickAddRowIndex,
    setQuickAddRowIndex,
    selectedStationIdentifier,
    setSelectedStationIdentifier,
    selectedStationId,
    setSelectedStationId,
    datasourcesData,
    handleQuickAddSource,
    handleCommentWidgetChange,
    onOpenWidgetSettings,
    onExportWidget,
    onExportWidgetExcel,
    moveWidget,
    dashboardId,
    onCopyWidget,
  }) => {
    const template = getWidgetTemplate(widget.key);
    const widgetTitle = widget.metaData?.title || template?.name || widget.key;
    const showHeader = editWidgets || !!widget.metaData?.title;
    const widgetRef = React.useRef<HTMLDivElement>(null);
    const dateRange: [Date, Date] = widget.dateRange
      ? [
          toUTC(new Date(widget.dateRange[0])),
          toUTC(new Date(widget.dateRange[1])),
        ]
      : [toUTC(new Date()), toUTC(new Date())];

    const [startDate, endDate] = dateRange;

    const isDragging = useDragSelector((state) => state.isDragging);
    const dragPayload = useDragSelector((state) => state.dragPayload);

    const isDraggingInScope = isDragging && dragPayload?.scope === identifier;

    const isBeingDragged =
      dragPayload?.scope === identifier &&
      dragPayload?.widgetIdx === widgetIndex &&
      dragPayload?.widgetGroupIdx === rowIndex;

    const isDragTarget = isDraggingInScope && !isBeingDragged;

    const draggedWidgetIdx = dragPayload?.widgetIdx;
    const draggedRowIdx = dragPayload?.widgetGroupIdx;
    const isSameRowAsDragged = draggedRowIdx === rowIndex;

    const showLeftZone =
      isDragTarget &&
      !(isSameRowAsDragged && draggedWidgetIdx === widgetIndex - 1);
    const showRightZone =
      isDragTarget &&
      !(isSameRowAsDragged && draggedWidgetIdx === widgetIndex + 1);

    const colSpan = widget.colSpan ?? DEFAULT_COL_SPAN;

    const { ref: dragRef } = useDraggable({
      payload: {
        widget,
        widgetIdx: widgetIndex,
        widgetGroupIdx: rowIndex,
        scope: identifier,
      },
      disabled: !editWidgets,
    });

    const dragWidth = isDraggingInScope
      ? calculateWidgetWidthDuringDrag(widget, row.widgets, DEFAULT_COL_SPAN)
      : undefined;

    const WidgetWrapper = dragWidth ? "div" : Col;
    const widgetWrapperProps = dragWidth
      ? {
          className: classNames("widget-column", {
            "widget-dragging": isBeingDragged,
            "widget-drag-mode": isDraggingInScope,
          }),
          style: {
            opacity: isBeingDragged ? 0.4 : 1,
            flex: `0 0 ${dragWidth}`,
            maxWidth: dragWidth,
            minWidth: 0,
            transition:
              "opacity 0.2s ease, flex 0.2s ease, max-width 0.2s ease",
          },
        }
      : {
          md: colSpan,
          xs: 12,
          className: classNames("widget-column", {
            "widget-dragging": isBeingDragged,
            "widget-drag-mode": isDraggingInScope,
          }),
          style: {
            opacity: isBeingDragged ? 0.4 : 1,
            transition:
              "opacity 0.2s ease, flex 0.2s ease, max-width 0.2s ease",
          },
        };

    return (
      <React.Fragment
        key={`widget_fragment_${widget.key}_${widgetIndex}_${rowIndex}`}
      >
        {showLeftZone && (
          <WidgetDropZone
            rowIndex={rowIndex}
            widgetIndex={widgetIndex}
            side="left"
            isFirst={true}
            scope={identifier}
          />
        )}
        <WidgetWrapper
          key={`widget_${rowIndex}_${widgetIndex}`}
          {...widgetWrapperProps}
        >
          <Card
            ref={widgetRef}
            className={classNames({
              "d-flex flex-column flex-fill overflow-hidden": !showHeader,
            })}
          >
            {showHeader && (
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  {editWidgets && (
                    <>
                      {!isTouchDevice && (
                        <div
                          ref={dragRef as React.RefCallback<HTMLDivElement>}
                          className="grip-icon-wrapper"
                          style={{
                            cursor: isBeingDragged ? "grabbing" : "grab",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faGripVertical}
                            className="text-muted drag-handle"
                          />
                        </div>
                      )}
                      {isTouchDevice && (
                        <div className="widget-arrow-controls d-flex gap-1">
                          <button
                            className="arrow-btn arrow-left desktop-only btn btn-sm btn-light"
                            onClick={() =>
                              moveWidget(
                                widgetIndex,
                                rowIndex,
                                "left",
                                isMobileViewport,
                              )
                            }
                            disabled={widgetIndex === 0}
                            title="Move left"
                          >
                            <FontAwesomeIcon icon={faArrowLeft} />
                          </button>
                          <button
                            className="arrow-btn arrow-up btn btn-sm btn-light"
                            onClick={() =>
                              moveWidget(
                                widgetIndex,
                                rowIndex,
                                "up",
                                isMobileViewport,
                              )
                            }
                            disabled={
                              isMobileViewport
                                ? widgetIndex === 0 && rowIndex === 0
                                : rowIndex === 0 && row.widgets.length === 1
                            }
                            title={
                              isMobileViewport ? "Move up / left" : "Move up"
                            }
                          >
                            <FontAwesomeIcon icon={faArrowUp} />
                          </button>
                          <button
                            className="arrow-btn arrow-down btn btn-sm btn-light"
                            onClick={() =>
                              moveWidget(
                                widgetIndex,
                                rowIndex,
                                "down",
                                isMobileViewport,
                              )
                            }
                            disabled={
                              isMobileViewport
                                ? rowIndex >=
                                    (stationWidgetRows?.length ?? 0) - 1 &&
                                  widgetIndex >= row.widgets.length - 1
                                : rowIndex >=
                                    (stationWidgetRows?.length ?? 0) - 1 &&
                                  row.widgets.length === 1
                            }
                            title={
                              isMobileViewport
                                ? "Move down / right"
                                : "Move down"
                            }
                          >
                            <FontAwesomeIcon icon={faArrowDown} />
                          </button>
                          <button
                            className="arrow-btn arrow-right desktop-only btn btn-sm btn-light"
                            onClick={() =>
                              moveWidget(
                                widgetIndex,
                                rowIndex,
                                "right",
                                isMobileViewport,
                              )
                            }
                            disabled={widgetIndex >= row.widgets.length - 1}
                            title="Move right"
                          >
                            <FontAwesomeIcon icon={faArrowRight} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  <span>{widgetTitle}</span>
                </div>
                {!editWidgets && (
                  <div className="d-flex gap-2">
                    {onExportWidget && (
                      <button
                        className="btn btn-link btn-sm p-0 text-muted widget-export-button"
                        onClick={() =>
                          widgetRef.current &&
                          onExportWidget(widgetRef.current, widgetTitle)
                        }
                        title={gettext("Export widget as JPEG")}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                      </button>
                    )}
                    {template?.allowExcelExport && onExportWidgetExcel && (
                      <button
                        className="btn btn-link btn-sm p-0 text-muted widget-export-button"
                        onClick={() => onExportWidgetExcel(widget)}
                        title={gettext("Export widget as Excel")}
                      >
                        <FontAwesomeIcon icon={faFileExcel} />
                      </button>
                    )}
                  </div>
                )}
              </Card.Header>
            )}

            {editWidgets && (
              <div className="widget-controls d-flex gap-2 px-3 py-2 bg-light border-bottom">
                {template?.supportsMultipleSources && (
                  <div
                    className="quick-add-button"
                    onClick={() =>
                      setQuickAddRowIndex(
                        quickAddRowIndex === rowIndex ? null : rowIndex,
                      )
                    }
                    title={gettext("Add parameter")}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </div>
                )}
                {template?.visualSettingsType === "table" && (
                  <div
                    className="settings-button"
                    onClick={() =>
                      onOpenWidgetSettings(
                        widget,
                        stationIndex,
                        rowIndex,
                        widgetIndex,
                      )
                    }
                    title={gettext("Widget settings")}
                  >
                    <FontAwesomeIcon icon={faCog} />
                  </div>
                )}
                <Dropdown className="resize-dropdown">
                  <Dropdown.Toggle
                    as="div"
                    className="resize-button"
                    title={gettext("Change widget size")}
                  >
                    <FontAwesomeIcon
                      icon={getSizePreset(widget.colSpan).icon}
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {SIZE_PRESETS.map((preset) => (
                      <Dropdown.Item
                        key={preset.key}
                        active={widget.colSpan === preset.colSpan}
                        onClick={() =>
                          handleWidgetResize(
                            rowIndex,
                            widgetIndex,
                            preset.colSpan,
                          )
                        }
                      >
                        <FontAwesomeIcon icon={preset.icon} className="me-2" />
                        {preset.label}
                        {preset.colSpan === DEFAULT_COL_SPAN && (
                          <span className="text-muted ms-1">
                            ({gettext("default")})
                          </span>
                        )}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <div
                  className="edit-widget-button"
                  onClick={() => showEditWidget(widget, rowIndex, widgetIndex)}
                  title={gettext("Edit widget")}
                >
                  <FontAwesomeIcon icon={faEdit} />
                </div>
                {onCopyWidget && (
                  <div
                    className="copy-widget-button"
                    onClick={() =>
                      onCopyWidget(widget, stationIndex, rowIndex, widgetIndex)
                    }
                    title={gettext("Copy to dashboard")}
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </div>
                )}
                <div
                  className="delete-widget-button"
                  onClick={() => onDeleteWidget(widgetIndex, rowIndex)}
                  title={gettext("Delete widget")}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </div>
              </div>
            )}

            <Card.Body
              className={classNames({
                "d-flex flex-column flex-fill p-0": !showHeader,
              })}
            >
              {!showHeader &&
                !editWidgets &&
                (onExportWidget ||
                  (template?.allowExcelExport && onExportWidgetExcel)) && (
                  <div
                    className="position-absolute top-0 end-0 d-flex gap-2"
                    style={{ zIndex: 10 }}
                  >
                    {onExportWidget && (
                      <button
                        className="btn btn-link btn-sm p-2 text-muted widget-export-button"
                        onClick={() =>
                          widgetRef.current &&
                          onExportWidget(widgetRef.current, widgetTitle)
                        }
                        title={gettext("Export widget as JPEG")}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                      </button>
                    )}
                    {template?.allowExcelExport && onExportWidgetExcel && (
                      <button
                        className="btn btn-link btn-sm p-2 text-muted widget-export-button"
                        onClick={() => onExportWidgetExcel(widget)}
                        title={gettext("Export widget as Excel")}
                      >
                        <FontAwesomeIcon icon={faFileExcel} />
                      </button>
                    )}
                  </div>
                )}
              {!STATIC_DATE_WIDGETS.includes(widget.key) && (
                <div
                  className={classNames("mb-3 px-3", {
                    "mt-3": !showHeader,
                  })}
                >
                  {editWidgets ? (
                    <>
                      <label className="form-label">
                        {gettext("Date range for this widget")}
                      </label>
                      <ControlledDateRangeSelector
                        startDate={startDate}
                        endDate={endDate}
                        onChange={(newRange) =>
                          onWidgetDateRangeChange(
                            stationIndex,
                            rowIndex,
                            widgetIndex,
                            newRange,
                          )
                        }
                      />
                    </>
                  ) : (
                    <div className="small text-primary">
                      {startDate.toLocaleDateString()} -{" "}
                      {endDate.toLocaleDateString()}
                    </div>
                  )}
                </div>
              )}

              {quickAddRowIndex === rowIndex && editWidgets && (
                <div className="mb-3 p-2 bg-light rounded">
                  <Form.Group>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label className="mb-0">
                        {gettext("Add parameter")}
                      </Form.Label>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={() => setQuickAddRowIndex(null)}
                      >
                        {gettext("Cancel")}
                      </Button>
                    </div>
                    <Form.Group className="mb-2">
                      <Form.Label className="small text-muted">
                        {gettext("Station")}
                      </Form.Label>
                      <SearchableStationPicker
                        byIdentifier
                        value={selectedStationIdentifier}
                        onChange={setSelectedStationIdentifier}
                        onStationSelect={(station) =>
                          setSelectedStationId(station?.id ?? null)
                        }
                        placeholder={gettext("Select station...")}
                      />
                    </Form.Group>
                    <Form.Label className="small text-muted">
                      {gettext("Data source")}
                    </Form.Label>
                    <Form.Select
                      onChange={(e) =>
                        handleQuickAddSource(
                          rowIndex,
                          widgetIndex,
                          e.target.value,
                        )
                      }
                      value=""
                    >
                      <option value="">
                        {gettext("Select a data source...")}
                      </option>
                      {datasourcesData?.datasource?.map((ds) => (
                        <option
                          key={ds.source_uuid}
                          value={String(ds.source_uuid)}
                        >
                          {ds.name}
                          {ds.unit?.symbol ? ` (${ds.unit.symbol})` : ""}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              )}

              {widget.key === "comment_widget" ? (
                <CommentWidget
                  title={widget.metaData.title || ""}
                  content={widget.metaData.content || ""}
                  fontSize={widget.metaData.fontSize}
                  fontColor={widget.metaData.fontColor}
                  backgroundColor={widget.metaData.backgroundColor}
                  editMode={editWidgets}
                  onChange={(newValues) =>
                    handleCommentWidgetChange(rowIndex, widgetIndex, newValues)
                  }
                />
              ) : (
                <StationDashboard
                  stationIdentifier={identifier}
                  stationId={stationId!}
                  sourceId={0}
                  sourceType="channel"
                  startDate={startDate}
                  endDate={endDate}
                  editedStructure={editedStructure}
                  setEditedStructure={setEditedStructure}
                  setChangesMade={setChangesMade}
                  canEditWidgets={editWidgets}
                  singleWidget={{
                    ...widget,
                    metaData: {
                      ...widget.metaData,
                      dashboardId,
                      widgetConfig: widget,
                    },
                  }}
                  canEditVisualSettings={canEditVisualSettings}
                  onOpenDatasourceSettings={(
                    sourceUUID: string,
                    sourceName: string,
                    sourceIndex: number,
                  ) =>
                    onOpenDatasourceSettings(
                      sourceUUID,
                      sourceName,
                      sourceIndex,
                      widget,
                      stationIndex,
                      rowIndex,
                      widgetIndex,
                    )
                  }
                />
              )}
            </Card.Body>
          </Card>
        </WidgetWrapper>
        {showRightZone && (
          <WidgetDropZone
            rowIndex={rowIndex}
            widgetIndex={widgetIndex}
            side="right"
            isFirst={false}
            scope={identifier}
          />
        )}
      </React.Fragment>
    );
  },
);

DraggableWidget.displayName = "DraggableWidget";

const ReportingDashboardStation = ({
  station,
  stationIndex,
  editWidgets,
  editedStructure,
  setEditedStructure,
  setChangesMade,
  onWidgetDateRangeChange,
  onOpenWidgetSettings,
  onOpenDatasourceSettings,
  canEditVisualSettings = false,
  onExportWidget,
  onExportWidgetExcel,
  dashboardId,
  onCopyWidget,
}: ReportingDashboardStationProps) => {
  const { identifier } = station;
  const { setActiveModal, setModalPayload } = useModalContext();
  const [quickAddRowIndex, setQuickAddRowIndex] = React.useState<number | null>(
    null,
  );
  const [selectedStationId, setSelectedStationId] = React.useState<
    number | null
  >(null);
  const [selectedStationIdentifier, setSelectedStationIdentifier] =
    React.useState<string>("");
  const [isTouchDevice] = React.useState(
    () => "ontouchstart" in window || navigator.maxTouchPoints > 0,
  );
  const isMobileViewport = useMediaQuery("(max-width: 767px)");
  const isDragging = useDragSelector(
    (state) => state.isDragging && state.dragPayload?.scope === identifier,
  );

  const { data } = useQuery<
    GetMeasurementStationMetaQuery,
    GetMeasurementStationMetaQueryVariables
  >(GET_MEASUREMENT_STATION_META, {
    variables: { identifier },
  });

  const stationMeta = data?.station?.[0];
  const stationId = stationMeta?.id;

  // Reset selected station to current station when quick add is opened
  React.useEffect(() => {
    if (quickAddRowIndex !== null && stationId && identifier) {
      setSelectedStationId(stationId);
      setSelectedStationIdentifier(identifier);
    }
  }, [quickAddRowIndex, stationId, identifier]);

  // Fetch datasources for quick add functionality from selected station
  const { data: datasourcesData } = useQuery<
    GetDatasourcesByStation,
    GetDatasourcesByStationVariables
  >(GET_DATASOURCES_BY_STATION, {
    variables: { stationId: Number(selectedStationId) },
    skip: !selectedStationId || quickAddRowIndex === null,
  });

  const stationIdx = stationMeta
    ? (editedStructure?.findIndex(
        (s: any) => s.station.identifier === identifier,
      ) ?? -1)
    : -1;

  const stationWidgetRows =
    editedStructure?.[stationIdx]?.station?.widget_rows ?? null;

  const handleDropOnZone = React.useCallback(
    (draggingWidget: DragPayload, position: DropPosition) => {
      if (!stationWidgetRows) {
        return;
      }

      const { newWidgetRows, hasChanges } = calculateWidgetDrop(
        stationWidgetRows,
        draggingWidget,
        position,
      );

      if (!hasChanges) {
        return;
      }

      const filteredStructure = insertIntoList(
        editedStructure,
        stationIdx,
        {
          station: {
            ...editedStructure?.[stationIdx]?.station,
            widget_rows: newWidgetRows,
          },
        },
        true,
      );

      setEditedStructure(filteredStructure);
      setChangesMade(true);
    },
    [
      stationWidgetRows,
      editedStructure,
      stationIdx,
      setEditedStructure,
      setChangesMade,
    ],
  );

  // Every station on the page receives every drop; only handle the ones that
  // start and end within this station.
  useScopedDragEnd(identifier, handleDropOnZone, !!editWidgets);

  const moveWidget = React.useCallback(
    (
      widgetIdx: number,
      rowIdx: number,
      direction: "left" | "right" | "up" | "down",
      isMobileViewport: boolean = false,
    ) => {
      const widget = stationWidgetRows?.[rowIdx]?.widgets?.[widgetIdx];
      if (!widget) {
        return;
      }

      const payload: DragPayload = {
        widget,
        widgetIdx,
        widgetGroupIdx: rowIdx,
        scope: identifier,
      };

      const rowWidgets = stationWidgetRows?.[rowIdx]?.widgets ?? [];
      const totalRows = stationWidgetRows?.length ?? 0;
      const isFirstInRow = widgetIdx === 0;
      const isLastInRow = widgetIdx >= rowWidgets.length - 1;

      if (direction === "left" && widgetIdx > 0) {
        handleDropOnZone(payload, {
          type: "widget",
          rowIndex: rowIdx,
          widgetIndex: widgetIdx - 1,
          side: "left",
        });
      } else if (direction === "right" && !isLastInRow) {
        handleDropOnZone(payload, {
          type: "widget",
          rowIndex: rowIdx,
          widgetIndex: widgetIdx + 1,
          side: "right",
        });
      } else if (direction === "up") {
        if (isMobileViewport && !isFirstInRow) {
          handleDropOnZone(payload, {
            type: "widget",
            rowIndex: rowIdx,
            widgetIndex: widgetIdx - 1,
            side: "left",
          });
        } else if (isMobileViewport && isFirstInRow && rowIdx > 0) {
          const prevRowWidgets = stationWidgetRows?.[rowIdx - 1]?.widgets ?? [];
          const targetIndex = Math.max(0, prevRowWidgets.length - 2);
          handleDropOnZone(payload, {
            type: "widget",
            rowIndex: rowIdx - 1,
            widgetIndex: targetIndex,
            side: "left",
          });
        } else if (!isMobileViewport && rowIdx > 0) {
          handleDropOnZone(payload, {
            type: "row",
            rowIndex: rowIdx - 1,
            isNewRow: false,
          });
        } else if (!isMobileViewport && rowIdx === 0) {
          handleDropOnZone(payload, {
            type: "row",
            rowIndex: 0,
            isNewRow: true,
          });
        }
      } else if (direction === "down") {
        if (isMobileViewport && !isLastInRow) {
          handleDropOnZone(payload, {
            type: "widget",
            rowIndex: rowIdx,
            widgetIndex: widgetIdx + 1,
            side: "right",
          });
        } else if (isMobileViewport && isLastInRow && rowIdx < totalRows - 1) {
          const nextRowWidgets = stationWidgetRows?.[rowIdx + 1]?.widgets ?? [];
          const targetIndex = nextRowWidgets.length > 0 ? 1 : 0;
          handleDropOnZone(payload, {
            type: "widget",
            rowIndex: rowIdx + 1,
            widgetIndex: targetIndex,
            side: "left",
          });
        } else if (!isMobileViewport && rowIdx < totalRows - 1) {
          handleDropOnZone(payload, {
            type: "row",
            rowIndex: rowIdx + 1,
            isNewRow: false,
          });
        } else if (!isMobileViewport && rowIdx >= totalRows - 1) {
          handleDropOnZone(payload, {
            type: "row",
            rowIndex: rowIdx + 1,
            isNewRow: true,
          });
        }
      }
    },
    [stationWidgetRows, handleDropOnZone, identifier],
  );

  if (!stationMeta) {
    return null;
  }

  const showAddWidget = (rowIndex: number) => {
    setActiveModal("add-dashboard-widget");
    setModalPayload({ stationIdentifier: identifier });
  };

  const showEditWidget = (
    widget: any,
    rowIndex: number,
    widgetIndex: number,
  ) => {
    setActiveModal("edit-dashboard-widget");
    setModalPayload({
      stationIdentifier: identifier,
      editingWidget: { widget, rowIndex, widgetIndex },
    });
  };

  const handleQuickAddSource = (
    rowIndex: number,
    widgetIndex: number,
    sourceUUID: string,
  ) => {
    if (!editedStructure || stationIdx < 0 || !sourceUUID) return;

    const newStructure = updateWidgetInStructure(
      editedStructure,
      stationIdx,
      rowIndex,
      widgetIndex,
      (widget) => {
        const newMetaData = { ...widget.metaData };
        if (newMetaData.sourceUUIDs) {
          newMetaData.sourceUUIDs = [...newMetaData.sourceUUIDs, sourceUUID];
        } else if (newMetaData.sourceUUID) {
          newMetaData.sourceUUIDs = [newMetaData.sourceUUID, sourceUUID];
          delete newMetaData.sourceUUID;
        } else {
          newMetaData.sourceUUIDs = [sourceUUID];
        }
        return { ...widget, metaData: newMetaData };
      },
    );
    if (newStructure === editedStructure) {
      return;
    }

    setEditedStructure(newStructure);
    setChangesMade(true);
    setQuickAddRowIndex(null);
  };

  const handleCommentWidgetChange = (
    rowIndex: number,
    widgetIndex: number,
    newValues: {
      title: string;
      content: string;
      fontSize: number;
      fontColor: string;
      backgroundColor: string;
    },
  ) => {
    if (!editedStructure || stationIdx < 0) return;

    const newStructure = updateWidgetInStructure(
      editedStructure,
      stationIdx,
      rowIndex,
      widgetIndex,
      (widget) => ({
        ...widget,
        metaData: { ...widget.metaData, ...newValues },
      }),
    );
    if (newStructure === editedStructure) {
      return;
    }
    setEditedStructure(newStructure);
    setChangesMade(true);
  };

  const handleWidgetResize = (
    rowIndex: number,
    widgetIndex: number,
    newColSpan: number,
  ) => {
    if (!editedStructure || stationIdx < 0) return;

    const newStructure = updateWidgetInStructure(
      editedStructure,
      stationIdx,
      rowIndex,
      widgetIndex,
      (widget) => ({ ...widget, colSpan: newColSpan }),
    );
    if (newStructure === editedStructure) {
      return;
    }
    setEditedStructure(newStructure);
    setChangesMade(true);
  };

  const onDeleteWidget = (idx: number, widgetGroupIdx: number) => {
    const newGroupWidgets = stationWidgetRows?.[widgetGroupIdx]?.widgets ?? [];
    const newWidgetGroup = newGroupWidgets.filter(
      (_: any, widgetIdx: number) => widgetIdx !== idx,
    );
    const newStructure = insertIntoList(
      stationWidgetRows,
      widgetGroupIdx,
      {
        widgets: newWidgetGroup,
      },
      true,
    );

    const filteredStructure = insertIntoList(
      editedStructure,
      stationIdx,
      {
        station: {
          ...editedStructure?.[stationIdx]?.station,
          widget_rows: newStructure,
        },
      },
      true,
    );
    setEditedStructure(filteredStructure);
    setChangesMade(true);
  };

  const widgetRows = stationWidgetRows || [];

  return (
    <div className="reporting-dashboard-station mb-4">
      <h4 className="mb-3">{stationMeta.name}</h4>

      {widgetRows.map((row: WidgetRowStructure, rowIndex: number) => (
        <React.Fragment key={`widget_row_fragment_${rowIndex}`}>
          <RowDropZone rowIndex={rowIndex} scope={identifier} />
          <Row
            key={`widget_row_${rowIndex}`}
            className={classNames("g-3 mb-3", { "row-drag-mode": isDragging })}
          >
            {row.widgets.map((widget: WidgetStructure, widgetIndex: number) => (
              <DraggableWidget
                key={`draggable_widget_${widget.key}_${widgetIndex}`}
                widget={widget}
                widgetIndex={widgetIndex}
                rowIndex={rowIndex}
                row={row}
                editWidgets={editWidgets}
                identifier={identifier}
                stationId={stationId}
                stationIndex={stationIndex}
                canEditVisualSettings={canEditVisualSettings}
                editedStructure={editedStructure}
                setEditedStructure={setEditedStructure}
                setChangesMade={setChangesMade}
                isTouchDevice={isTouchDevice}
                isMobileViewport={isMobileViewport}
                stationWidgetRows={stationWidgetRows}
                onOpenDatasourceSettings={onOpenDatasourceSettings}
                onWidgetDateRangeChange={onWidgetDateRangeChange}
                showEditWidget={showEditWidget}
                handleWidgetResize={handleWidgetResize}
                onDeleteWidget={onDeleteWidget}
                quickAddRowIndex={quickAddRowIndex}
                setQuickAddRowIndex={setQuickAddRowIndex}
                selectedStationIdentifier={selectedStationIdentifier}
                setSelectedStationIdentifier={setSelectedStationIdentifier}
                selectedStationId={selectedStationId}
                setSelectedStationId={setSelectedStationId}
                datasourcesData={datasourcesData}
                handleQuickAddSource={handleQuickAddSource}
                handleCommentWidgetChange={handleCommentWidgetChange}
                onOpenWidgetSettings={onOpenWidgetSettings}
                onExportWidget={onExportWidget}
                onExportWidgetExcel={onExportWidgetExcel}
                moveWidget={moveWidget}
                dashboardId={dashboardId}
                onCopyWidget={onCopyWidget}
              />
            ))}
          </Row>
        </React.Fragment>
      ))}

      <RowDropZone rowIndex={widgetRows.length} scope={identifier} />

      {editWidgets && (
        <Row key="add_new_widget" className="g-3 mb-3 row-add-widget">
          <Col>
            <Card>
              <Card.Body
                className="add-widget-button"
                onClick={() => showAddWidget(widgetRows.length)}
              >
                <h5>+ Add widget</h5>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ReportingDashboardStation;
